import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";

interface AutoTag {
  slug: string;
  label: string;
  eixo: string;
  confidence: number;
  evidencia: string | null;
  fontes: string[] | null;
}

const EIXO_LABEL: Record<string, string> = {
  especialidade: "Especialidades",
  setor_cliente: "Setores de cliente",
  ferramenta: "Ferramentas",
};

const EIXO_ORDER = ["especialidade", "setor_cliente", "ferramenta"];

/**
 * Tags automáticas extraídas por IA (v_freelancer_tags_atual, M173) no detalhe
 * do freelancer. Visual distinto das tags manuais (que seguem editáveis no
 * bloco acima): somente leitura, agrupadas por eixo, com a evidência literal
 * que justificou cada tag no tooltip.
 */
export function FreelancerAutoTags({ freelancerId }: { freelancerId: string }) {
  const { data: autoTags = [] } = useQuery({
    queryKey: ["freelancer-auto-tags", freelancerId],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<AutoTag[]> => {
      const { data, error } = await supabase
        .from("v_freelancer_tags_atual" as never)
        .select("slug,label,eixo,confidence,evidencia,fontes")
        .eq("freelancer_id", freelancerId)
        .order("eixo")
        .order("label");
      if (error) throw error;
      return (data ?? []) as unknown as AutoTag[];
    },
  });

  if (autoTags.length === 0) return null;

  const porEixo = EIXO_ORDER.map((eixo) => ({
    eixo,
    tags: autoTags.filter((tag) => tag.eixo === eixo),
  })).filter((grupo) => grupo.tags.length > 0);

  return (
    <div className="space-y-1.5">
      <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5" /> Tags automáticas (IA)
      </h3>
      <TooltipProvider>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {porEixo.map(({ eixo, tags }) => (
            <div key={eixo} className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground/70">
                {EIXO_LABEL[eixo] ?? eixo}:
              </span>
              {tags.map((tag) => (
                <Tooltip key={tag.slug}>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="cursor-default font-normal text-muted-foreground"
                    >
                      {tag.label}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <div className="space-y-1">
                      {tag.evidencia && (
                        <p className="italic">"{tag.evidencia}"</p>
                      )}
                      <p className="text-[11px] opacity-80">
                        Confiança: {Math.round(tag.confidence * 100)}%
                        {tag.fontes && tag.fontes.length > 0 && (
                          <> · Fonte: {tag.fontes.join(", ")}</>
                        )}
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          ))}
        </div>
      </TooltipProvider>
    </div>
  );
}
